import type { IAppRegion } from '~/lib/appSettings';
import type { ApiClient, IApiJsonRequest } from '../../ApiClient';
import type {
  IApiCreateInvitationRequestBody,
  IApiCreateInvitationResponseBody,
  IApiGetInvitationResponseBody,
  IApiGetInvitationsQueryStringParams,
  IApiGetInvitationsResponseBody,
  IApiRespondInvitationRequestBody,
  IApiUpdateInvitationResponseBody,
} from './InvitationsApi.types';

export class InvitationsApi {
  public constructor(private client: ApiClient) {}

  public async create(
    body: IApiCreateInvitationRequestBody,
  ): Promise<IApiCreateInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: '/invitations',
      method: 'post',
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiCreateInvitationResponseBody>(
        request,
      );

    return data;
  }

  public async get(
    invitationId: string,
  ): Promise<IApiGetInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: `/invitations/${invitationId}`,
      method: 'get',
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetInvitationResponseBody>(
        request,
      );

    return data;
  }

  public async getPublic(
    invitationId: string,
    region: IAppRegion,
  ): Promise<IApiGetInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: `/invitations/${invitationId}`,
      method: 'get',
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetInvitationResponseBody>(
        request,
        { skipAuth: true, region },
      );

    return data;
  }

  public async getAllForUser(
    params?: IApiGetInvitationsQueryStringParams,
  ): Promise<IApiGetInvitationsResponseBody> {
    const request: IApiJsonRequest = {
      path: '/invitations',
      method: 'get',
      params,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetInvitationsResponseBody>(
        request,
      );

    return data;
  }

  public async getAllForOrganization(
    organizationId: string,
    params?: IApiGetInvitationsQueryStringParams,
  ): Promise<IApiGetInvitationsResponseBody> {
    const request: IApiJsonRequest = {
      path: `/organizations/${organizationId}/invitations`,
      method: 'get',
      params,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiGetInvitationsResponseBody>(
        request,
      );

    return data;
  }

  public async accept(
    invitationId: string,
    body: IApiRespondInvitationRequestBody,
  ): Promise<IApiUpdateInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: `/invitations/${invitationId}/accept`,
      method: 'put',
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiUpdateInvitationResponseBody>(
        request,
      );

    return data;
  }

  public async decline(
    invitationId: string,
    region: IAppRegion,
    body: IApiRespondInvitationRequestBody,
  ): Promise<IApiUpdateInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: `/invitations/${invitationId}/decline`,
      method: 'put',
      body,
    };

    const data =
      await this.client.fetchJsonStandardized<IApiUpdateInvitationResponseBody>(
        request,
        { skipAuth: true, region },
      );

    return data;
  }

  public async resend(
    invitationId: string,
  ): Promise<IApiUpdateInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: `/invitations/${invitationId}/resend`,
      method: 'put',
    };

    const data =
      await this.client.fetchJsonStandardized<IApiUpdateInvitationResponseBody>(
        request,
      );

    return data;
  }

  public async cancel(
    invitationId: string,
  ): Promise<IApiUpdateInvitationResponseBody> {
    const request: IApiJsonRequest = {
      path: `/invitations/${invitationId}/cancel`,
      method: 'put',
    };

    const data =
      await this.client.fetchJsonStandardized<IApiUpdateInvitationResponseBody>(
        request,
      );

    return data;
  }
}
